import Avatar from '@material-ui/core/Avatar';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import { TnTypography } from '../TnTypography';

export type AvatarURLDetail = {
  avatarURL?: string;
  isCompleteURLPath?: boolean;
};

interface IProps {
  src?: string;
  name: string;
  onClick?: any;
  className?: any;
  isCompletePath?: boolean;
  size?: 'small' | 'medium' | 'large';
  margin?: number;
  isActive?: boolean;
  backgroundColor?: string;
  customTypography?: boolean;
}

const getBorderColor = (props: IProps, theme: any) => {
  if (props.isActive === true) {
    return props?.src ? 'rgba(0,0,0,0)' : theme.palette.divider;
  } else {
    return theme.palette.primary.main;
  }
};

function getInitials(name: string) {
  if (name) {
    let initials: any = name.match(/\b\w/g) || [];
    initials = ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();

    return initials;
  }

  return '';
}

export default function TnAvatar(props: IProps) {
  const theme = useTheme<Theme>();
  const classes = useStyles(props);
  const [imgUrl, setImgUrl] = useState(props && props.src);

  useEffect(() => {
    setImgUrl(props.src);
  }, [props.src]);

  function addDefaultSrc() {
    setImgUrl('');
  }

  const getHashOfString = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);

    return hash;
  };

  const normalizeHash = (hash: number, min: number, max: number) => {
    return Math.floor((hash % (max - min)) + min);
  };

  const generateHSL = (name: string) => {
    const hash = getHashOfString(name);
    const hRange = [0, 360];
    const sRange = [35, 75];
    const lRange = [35, 75];
    const h = normalizeHash(hash, hRange[0], hRange[1]);
    const s = normalizeHash(hash, sRange[0], sRange[1]);
    const l = normalizeHash(hash, lRange[0], lRange[1]);

    return HSLtoString([h, s, l]);
  };

  const HSLtoString = (hsl: Array<number>) => {
    return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;
  };

  const getSrcLink = () => {
    let srcLink = '';
    let srcLinkFragment = '';

    if (imgUrl) {
      if (!props?.isCompletePath) {
        srcLinkFragment = `${process.env.PROFILE_PIC_URL ?? ''}`;
      }
      srcLink = srcLinkFragment + props?.src;
    }

    return srcLink;
  };

  const getAvatarBgColor = () => {
    if (props?.backgroundColor) {
      return props.backgroundColor;
    }

    return generateHSL(props?.name);
  };

  return (
    <Avatar
      style={{
        backgroundColor: getAvatarBgColor(),
        color: theme.palette.primary.contrastText,
      }}
      className={props.className ? classNames(classes.avatar, props.className) : classes.avatar}
      onClick={props.onClick}
      onError={addDefaultSrc}
      src={getSrcLink()}
    >
      {props.customTypography ? (
        <TnTypography color="grey" variant="caption4">
          {getInitials(props.name)}
        </TnTypography>
      ) : (
        getInitials(props.name)
      )}
    </Avatar>
  );
}

const useStyles = makeStyles((theme) => ({
  avatar: {
    margin: (props: IProps) => props.margin ?? 10,
    alignItems: 'center',
    width: (props: IProps) => (props.size === 'small' ? 24 : 44),
    height: (props: IProps) => (props.size === 'small' ? 24 : 44),
    fontSize: (props: IProps) => (props.size === 'small' ? 10 : undefined),
    borderColor: (props: IProps) => getBorderColor(props, theme),
    borderRadius: '250px',
  },
  imageBroke: {
    display: 'none',
  },
}));
